import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Chip,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Home as HomeIcon,
  Business as BusinessIcon,
  Search as SearchIcon,
  AccountBalance as EscrowIcon,
  Gavel as LegalIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { BUSINESS, PAGE_META } from '../constants/business';

const services = [
  {
    title: 'Title Insurance',
    description: 'Comprehensive protection for your most important investment',
    icon: SecurityIcon,
    slug: 'title-insurance',
    features: [
      'Owner\'s Title Insurance Policies',
      'Lender\'s Title Insurance Policies',
      'Extended Coverage Options',
      'Title Search & Examination',
      'Claims Resolution & Support',
    ],
    details: 'Title insurance protects you from financial loss due to defects in your property\'s title that may have occurred before you purchased it. Unlike other insurance that protects against future events, title insurance protects against past events that could affect your ownership rights. We explain every aspect of your policy and ensure you understand exactly what protection you\'re receiving.',
    process: 'We conduct a comprehensive title search, examine all records, clear any issues, and issue your policy at closing. You\'ll receive your original policy documents promptly after closing, which should be kept in a safe place as proof of your coverage.',
  },
  {
    title: 'Real Estate Closings',
    description: 'Professional, personalized closing services from contract to keys',
    icon: HomeIcon,
    slug: 'closings',
    features: [
      'Residential Purchase Closings',
      'Refinance Closings',
      'Commercial Property Closings',
      'Construction-to-Permanent Loans',
      'Document Preparation & Review',
    ],
    details: 'A real estate closing is the final step in your property transaction where ownership officially transfers from seller to buyer. We coordinate with all parties—buyers, sellers, real estate agents, lenders, and attorneys—to ensure every document is properly prepared and executed. Our team explains each document before you sign and answers all your questions.',
    process: 'From contract to closing, we maintain constant communication, provide regular updates, schedule your closing at your convenience, and ensure all funds are properly disbursed. You\'ll receive all your important documents, including your deed, immediately after closing.',
  },
  {
    title: 'Title Searches',
    description: 'Thorough investigation of your property\'s ownership history',
    icon: SearchIcon,
    slug: 'title-searches',
    features: [
      'Complete Chain of Title Analysis',
      'Municipal Lien & Tax Searches',
      'Judgment & Bankruptcy Searches',
      'Survey Review & Analysis',
      'Easement & Restriction Research',
    ],
    details: 'A title search is a detailed examination of public records to verify the legal ownership of a property and identify any claims, liens, or other issues that could affect your ownership. In New Jersey, this process involves searching county records, municipal records, state and federal tax records, and court records to ensure clear title.',
    process: 'Our experienced searchers examine decades of property records, identify and resolve any issues before closing, provide you with a comprehensive report of findings, and work with all parties to clear any title defects. We keep you informed throughout the process and explain any issues we discover.',
  },
  {
    title: 'Escrow Services',
    description: 'Secure, licensed handling of your transaction funds and documents',
    icon: EscrowIcon,
    slug: 'escrow',
    features: [
      'Purchase & Sale Escrows',
      'Refinance Escrows',
      'Construction Loan Escrows',
      'Secure Wire Transfers',
      'Document Safekeeping',
    ],
    details: 'Escrow services provide a secure, neutral third-party handling of funds and documents during your real estate transaction. We hold your earnest money, down payment, and closing funds in secure, segregated accounts until all conditions are met and the transaction can close safely.',
    process: 'We maintain strict security protocols, provide detailed accounting of all funds, coordinate with all parties to ensure proper timing, and disburse funds only when all conditions are satisfied. You\'ll receive complete documentation of all fund transfers and can contact us with questions at any time.',
  },
  {
    title: 'Commercial Services',
    description: 'Specialized expertise for complex commercial real estate transactions',
    icon: BusinessIcon,
    slug: 'commercial',
    features: [
      'Multi-Unit Property Transactions',
      'Commercial Title Insurance',
      '1031 Tax-Free Exchanges',
      'Construction & Development',
      'Portfolio Acquisitions',
    ],
    details: 'Commercial real estate transactions involve unique complexities requiring specialized knowledge of zoning laws, environmental issues, leases, and financing structures. Our team has extensive experience handling large, complex transactions while maintaining the personal service and attention to detail you deserve.',
    process: 'We analyze all aspects of the commercial transaction, coordinate with multiple parties and professionals, provide detailed title commitments and surveys, and ensure all regulatory requirements are met. Our goal is to facilitate your investment while protecting your interests throughout the process.',
  },
  {
    title: 'Document Delivery & Support',
    description: 'Comprehensive support beyond closing with secure document delivery',
    icon: LegalIcon,
    slug: 'document-support',
    features: [
      'Original Document Delivery',
      'Secure Document Storage Guidance',
      'Post-Closing Support',
      'Document Replacement Services',
      'Ongoing Client Communication',
    ],
    details: 'Your closing documents—especially your deed and title insurance policy—are among your most important legal documents. We ensure you receive all original documents promptly after closing and provide guidance on proper storage and safekeeping. These documents prove your ownership and provide your insurance coverage.',
    process: 'We prepare and deliver all documents securely, provide guidance on document storage and safekeeping, remain available for questions long after closing, and assist with document replacement if needed. Your peace of mind is our priority, and we\'re here to help whenever you need us.',
  },
];

const Services: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>{PAGE_META["/services"].title}</title>
        <meta name="description" content={PAGE_META["/services"].description} />
        <meta property="og:title" content={PAGE_META["/services"].title} />
        <meta property="og:description" content={PAGE_META["/services"].description} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={BUSINESS.displayName} />
        <link rel="canonical" href={`${BUSINESS.siteUrl}/services`} />
      </Helmet>

      {/* Hero Section */}
      <Box
        sx={{
          backgroundColor: '#1e3a8a',
          color: 'white',
          py: { xs: 6, md: 8 },
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h1"
              component="h1"
              sx={{
                fontWeight: 700,
                mb: 2,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                color: 'white',
              }}
            >
              Our Services
            </Typography>
            <Typography variant="h5" sx={{ maxWidth: 800, mx: 'auto', opacity: 0.9, color: '#94a3b8' }}>
              Comprehensive title services designed to protect your real estate investments
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Services Grid */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={4}>
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <Grid item xs={12} md={6} key={index}>
                <Card
                  className="hover-lift"
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.3s ease',
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
                      <Avatar
                        sx={{
                          width: 64,
                          height: 64,
                          mr: 2,
                          backgroundColor: '#1e3a8a',
                        }}
                      >
                        <Icon sx={{ fontSize: 32, color: 'white' }} />
                      </Avatar>
                      <Box>
                        <Typography variant="h5" component="h2" sx={{ mb: 1, fontWeight: 600 }}>
                          {service.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {service.description}
                        </Typography>
                      </Box>
                    </Box>

                    <Typography variant="body1" paragraph sx={{ lineHeight: 1.7 }}>
                      {service.details}
                    </Typography>

                    {service.process && (
                      <>
                        <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: '#64748b' }}>
                          Our Process:
                        </Typography>
                        <Typography variant="body2" paragraph sx={{ mb: 3, lineHeight: 1.6, color: 'text.secondary' }}>
                          {service.process}
                        </Typography>
                      </>
                    )}

                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      What's Included:
                    </Typography>
                    <List dense>
                      {service.features.map((feature, featureIndex) => (
                        <ListItem key={featureIndex} sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CheckIcon sx={{ fontSize: 16, color: '#f59e0b' }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={feature}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      ))}
                    </List>

                    <Box sx={{ mt: 3 }}>
                      <Button
                        variant="contained"
                        component={Link}
                        to={`/services/${service.slug}`}
                        sx={{
                          mr: 2,
                          mb: 1,
                        }}
                      >
                        Learn More
                      </Button>
                      <Button
                        variant="outlined"
                        component={Link}
                        to="/contact"
                        sx={{ mb: 1 }}
                      >
                        Get Quote
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Container>

      {/* Why Choose Us Section */}
      <Box sx={{ backgroundColor: '#f8fafc', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h2" component="h2" sx={{ mb: 2, color: '#64748b' }}>
              Why Choose MAD Title Agency?
            </Typography>
            <Typography variant="h6" sx={{ maxWidth: 600, mx: 'auto', color: 'text.secondary' }}>
              Experience the difference of working with New Jersey's trusted title professionals
            </Typography>
          </Box>

          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    mx: 'auto',
                    mb: 2,
                    backgroundColor: '#f59e0b',
                  }}
                >
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'white' }}>
                    48
                  </Typography>
                </Avatar>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  Hour Turnaround
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Fast processing without compromising accuracy or thoroughness
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    mx: 'auto',
                    mb: 2,
                    backgroundColor: '#f59e0b',
                  }}
                >
                  <SecurityIcon sx={{ fontSize: 40, color: 'white' }} />
                </Avatar>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  Licensed & Insured
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Fully licensed title agents with comprehensive E&O insurance
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    mx: 'auto',
                    mb: 2,
                    backgroundColor: '#f59e0b',
                  }}
                >
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'white' }}>
                    15+
                  </Typography>
                </Avatar>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  Years Experience
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Decades of combined experience in New Jersey real estate
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Detailed Service Process Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h2" component="h2" sx={{ mb: 2, color: '#64748b', fontWeight: 700 }}>
            Our Comprehensive Title Process
          </Typography>
          <Typography variant="h6" sx={{ maxWidth: 700, mx: 'auto', color: 'text.secondary', lineHeight: 1.6 }}>
            From initial order to final closing, we ensure every step is handled with precision and care
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {[
            {
              step: "1",
              title: "Title Search & Examination",
              description: "Our certified title professionals conduct a comprehensive search of public records dating back 50+ years, examining deeds, mortgages, liens, judgments, and other documents affecting the property.",
              details: "We search multiple sources including county clerk offices, tax assessor records, court filings, and municipal records to ensure complete title history.",
              timeframe: "24-48 hours"
            },
            {
              step: "2", 
              title: "Title Report Preparation",
              description: "We prepare a detailed title report identifying all parties with interest in the property, outstanding liens, easements, restrictions, and any title defects that need resolution.",
              details: "Our reports include complete legal descriptions, ownership history, and clear identification of any issues requiring attention before closing.",
              timeframe: "Same day as search"
            },
            {
              step: "3",
              title: "Issue Resolution & Curative",
              description: "Any title defects discovered during our search are immediately addressed. We work with all parties to resolve liens, correct documentation errors, and clear title issues.",
              details: "Common resolutions include lien payoffs, corrective deeds, probate clearances, and boundary line agreements. We handle all necessary paperwork and coordination.",
              timeframe: "1-5 days depending on complexity"
            },
            {
              step: "4",
              title: "Title Commitment Issued", 
              description: "Once title is clear, we issue a title commitment outlining the terms and conditions under which we'll provide title insurance coverage at closing.",
              details: "The commitment includes all standard and special exceptions, required documents, and any conditions that must be met before closing.",
              timeframe: "Within 24 hours of clearance"
            },
            {
              step: "5",
              title: "Closing Coordination",
              description: "We coordinate with all parties including lenders, real estate agents, attorneys, and clients to schedule closing and ensure all documents are properly prepared.",
              details: "Our team handles document preparation, fund coordination, recording preparation, and final title insurance policy issuance.",
              timeframe: "Closing day + 30 days for final policies"
            }
          ].map((processStep, index) => (
            <Grid item xs={12} key={index}>
              <Paper
                elevation={2}
                sx={{
                  p: 4,
                  border: '1px solid #e2e8f0',
                  borderRadius: 3,
                  position: 'relative',
                  '&:hover': {
                    boxShadow: '0 8px 24px rgba(30, 58, 138, 0.1)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      backgroundColor: '#1e3a8a',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                      {processStep.step}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h5" component="h3" sx={{ color: '#1e3a8a', fontWeight: 600 }}>
                        {processStep.title}
                      </Typography>
                      <Chip
                        label={processStep.timeframe}
                        size="small"
                        sx={{
                          backgroundColor: '#f59e0b',
                          color: 'white',
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                    <Typography variant="body1" paragraph sx={{ lineHeight: 1.7, color: '#374151', fontSize: '1.1rem' }}>
                      {processStep.description}
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.6, color: '#6b7280' }}>
                      {processStep.details}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* New Jersey Specific Requirements */}
      <Box sx={{ backgroundColor: '#f8fafc', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h2" component="h2" sx={{ mb: 2, color: '#64748b', fontWeight: 700 }}>
              New Jersey Title Requirements & Considerations
            </Typography>
            <Typography variant="h6" sx={{ maxWidth: 800, mx: 'auto', color: 'text.secondary', lineHeight: 1.6 }}>
              Understanding the unique aspects of New Jersey real estate law and local municipal requirements
            </Typography>
          </Box>

          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: '100%',
                  p: 4,
                  border: '1px solid #e2e8f0',
                  borderRadius: 3,
                }}
              >
                <Typography variant="h5" component="h3" sx={{ mb: 3, color: '#1e3a8a', fontWeight: 600 }}>
                  New Jersey Recording Requirements
                </Typography>
                <Typography variant="body1" paragraph sx={{ lineHeight: 1.7, color: '#374151' }}>
                  New Jersey requires all real estate documents to be recorded with the county clerk where the property 
                  is located. Each of the 21 counties has specific requirements for recording, fees, and processing times.
                </Typography>
                <Typography variant="body1" paragraph sx={{ lineHeight: 1.7, color: '#374151' }}>
                  <strong>Middlesex County specifics:</strong> Documents must include proper acknowledgments, tax maps, 
                  and comply with formatting requirements. Recording fees are based on document type and number of pages.
                </Typography>
                <Typography variant="body1" sx={{ lineHeight: 1.7, color: '#374151' }}>
                  Our team stays current with all county-specific requirements to ensure smooth recording and avoid delays.
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: '100%',
                  p: 4,
                  border: '1px solid #e2e8f0',
                  borderRadius: 3,
                }}
              >
                <Typography variant="h5" component="h3" sx={{ mb: 3, color: '#1e3a8a', fontWeight: 600 }}>
                  Realty Transfer Fee & Tax Considerations
                </Typography>
                <Typography variant="body1" paragraph sx={{ lineHeight: 1.7, color: '#374151' }}>
                  New Jersey imposes a Realty Transfer Fee of $5.15 per $1,000 of consideration on most property transfers. 
                  Additionally, many municipalities impose local transfer taxes or fees.
                </Typography>
                <Typography variant="body1" paragraph sx={{ lineHeight: 1.7, color: '#374151' }}>
                  <strong>Local variations:</strong> Woodbridge Township, Edison, and other Middlesex County municipalities 
                  may have additional requirements including municipal liens searches and certifications.
                </Typography>
                <Typography variant="body1" sx={{ lineHeight: 1.7, color: '#374151' }}>
                  We calculate all applicable fees and ensure compliance with state and local tax requirements.
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card
                sx={{
                  p: 4,
                  border: '1px solid #e2e8f0',
                  borderRadius: 3,
                  backgroundColor: '#fefdfb',
                }}
              >
                <Typography variant="h5" component="h3" sx={{ mb: 3, color: '#1e3a8a', fontWeight: 600 }}>
                  Municipal Liens & Certificate Requirements
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="h6" sx={{ mb: 2, color: '#f59e0b', fontWeight: 600 }}>
                      Required Certificates
                    </Typography>
                    <List dense>
                      {[
                        'Tax certificate from municipal tax collector',
                        'Water/sewer certificate if applicable', 
                        'Municipal lien search certificate',
                        'Fire department certificate (some municipalities)',
                        'Certificate of occupancy verification'
                      ].map((cert, index) => (
                        <ListItem key={index} sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 24 }}>
                            <CheckIcon sx={{ fontSize: 16, color: '#f59e0b' }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={cert}
                            primaryTypographyProps={{ variant: 'body2', sx: { fontSize: '0.95rem' } }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="h6" sx={{ mb: 2, color: '#f59e0b', fontWeight: 600 }}>
                      Common Municipal Liens
                    </Typography>
                    <List dense>
                      {[
                        'Unpaid property taxes and assessments',
                        'Water and sewer charges',
                        'Code enforcement violations',
                        'Snow removal and municipal services',
                        'Special assessments for improvements'
                      ].map((lien, index) => (
                        <ListItem key={index} sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 24 }}>
                            <CheckIcon sx={{ fontSize: 16, color: '#f59e0b' }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={lien}
                            primaryTypographyProps={{ variant: 'body2', sx: { fontSize: '0.95rem' } }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="h6" sx={{ mb: 2, color: '#f59e0b', fontWeight: 600 }}>
                      Processing Timeline
                    </Typography>
                    <Typography variant="body2" paragraph sx={{ lineHeight: 1.6, color: '#374151' }}>
                      Most municipal certificates can be obtained within 24-48 hours. However, some municipalities 
                      require additional processing time, especially for older properties or those with complex histories.
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.6, color: '#374151' }}>
                      We maintain relationships with municipal offices throughout our service area to expedite 
                      certificate processing and resolve issues quickly.
                    </Typography>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Service Area Coverage */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h2" component="h2" sx={{ mb: 2, color: '#64748b', fontWeight: 700 }}>
            Comprehensive Coverage Across Eastern New Jersey
          </Typography>
          <Typography variant="h6" sx={{ maxWidth: 600, mx: 'auto', color: 'text.secondary', lineHeight: 1.6 }}>
            Professional title services for residential and commercial properties throughout our service region
          </Typography>
        </Box>

        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={3}
              sx={{
                p: 4,
                height: '100%',
                border: '1px solid #e2e8f0',
                borderRadius: 3,
                background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                color: 'white',
              }}
            >
              <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 600 }}>
                Primary Service Counties
              </Typography>
              <Grid container spacing={2}>
                {[
                  'Middlesex County - Woodbridge, Edison, New Brunswick, Perth Amboy',
                  'Bergen County - Full county coverage',
                  'Essex County - Newark, Bloomfield, Montclair',
                  'Union County - Elizabeth, Westfield, Cranford',
                  'Monmouth County - Freehold, Red Bank, Asbury Park',
                  'Warren County - Washington, Belvidere, Hackettstown'
                ].map((area, index) => (
                  <Grid item xs={12} key={index}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CheckIcon sx={{ fontSize: 20, color: '#f59e0b', mr: 2 }} />
                      <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                        {area}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={3}
              sx={{
                p: 4,
                height: '100%',
                border: '1px solid #e2e8f0',
                borderRadius: 3,
              }}
            >
              <Typography variant="h4" component="h3" sx={{ mb: 3, color: '#1e3a8a', fontWeight: 600 }}>
                Specialized Expertise
              </Typography>
              <Typography variant="body1" paragraph sx={{ lineHeight: 1.7, color: '#374151' }}>
                <strong>Historic Properties:</strong> Extensive experience with properties dating back to colonial times, 
                including complex chain of title issues and historical documentation requirements.
              </Typography>
              <Typography variant="body1" paragraph sx={{ lineHeight: 1.7, color: '#374151' }}>
                <strong>New Construction:</strong> Specialized knowledge of new development requirements, subdivision 
                approvals, and municipal compliance for newly constructed properties.
              </Typography>
              <Typography variant="body1" paragraph sx={{ lineHeight: 1.7, color: '#374151' }}>
                <strong>Commercial Properties:</strong> Experience with complex commercial transactions, including 
                industrial properties, retail centers, and mixed-use developments.
              </Typography>
              <Typography variant="body1" sx={{ lineHeight: 1.7, color: '#374151' }}>
                <strong>Waterfront Properties:</strong> Understanding of riparian rights, bulkhead lines, and 
                environmental considerations for properties along New Jersey's waterways.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box sx={{ backgroundColor: '#1e3a8a', color: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h3" component="h2" sx={{ mb: 2, fontWeight: 600 }}>
              Ready to Get Started?
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
              Contact us today for a free quote on any of our professional title services throughout New Jersey
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                component={Link}
                to="/contact"
                sx={{
                  backgroundColor: '#f59e0b',
                  color: '#1f2937',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  '&:hover': {
                    backgroundColor: '#d97706',
                  },
                }}
              >
                Get Free Quote
              </Button>
              <Button
                variant="outlined"
                size="large"
                href="tel:732-874-5425"
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderColor: 'white',
                  },
                }}
              >
                Call (732) 874-5425
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default Services; 